import { Color } from '../../../app/StyleGuide';
import { AssessmentState } from '../models/assessment';
import {
  AssessmentReportQuestion,
  AssessmentReportQuestionOption,
  AssessmentReportSummaryResponse,
} from '../models/assessmentReport';
import {
  AssessmentReportViewModel,
  CategoryBreakdown,
  CategoryQuestion,
  KeyInsight,
  LikertResponse,
} from '../models/assessmentReportView';
import { getScoreLevel, SCORE_LEVEL_LABELS } from './scoreUtils';

const getDistributionTotal = (options: Record<string, AssessmentReportQuestionOption>): number =>
  Object.values(options).reduce((sum, option) => sum + option.distribution, 0);

const getDistributionPercent = (count: number, total: number): number =>
  total === 0 ? 0 : Math.round((count / total) * 100);

const getIdealAndPositivePercents = (options: Record<string, AssessmentReportQuestionOption>) => {
  const total = getDistributionTotal(options);

  if (total === 0) {
    return { idealPercent: 0, positivePercent: 0 };
  }

  const idealPercent = getDistributionPercent(options['5']?.distribution ?? 0, total);
  const positivePercent = getDistributionPercent(
    (options['5']?.distribution ?? 0) + (options['4']?.distribution ?? 0),
    total
  );

  return { idealPercent, positivePercent };
};

const buildLikertResponses = (
  options: Record<string, AssessmentReportQuestionOption>,
  respondentCount: number
): LikertResponse[] => {
  const total = getDistributionTotal(options) || respondentCount;

  return Object.entries(options)
    .sort(([leftScore], [rightScore]) => Number(rightScore) - Number(leftScore))
    .map(([, option]) => ({
      label: option.text,
      count: option.distribution,
      percent: getDistributionPercent(option.distribution, total),
    }));
};

const buildCategoryQuestion = (question: AssessmentReportQuestion, respondentCount: number): CategoryQuestion => {
  const { idealPercent, positivePercent } = getIdealAndPositivePercents(question.options);

  return {
    text: question.text,
    score: question.score,
    idealPercent,
    positivePercent,
    responses: buildLikertResponses(question.options, respondentCount),
  };
};

const average = (values: number[]): number =>
  values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;

const buildCategoryDescription = (score: number): string => SCORE_LEVEL_LABELS[getScoreLevel(score)];

const buildOverallSummary = (categories: CategoryBreakdown[]): string => {
  if (categories.length === 0) {
    return 'Assessment results are available for your congregation.';
  }

  const sorted = [...categories].sort((left, right) => right.score - left.score);
  const strongest = sorted[0];
  const growthArea = sorted[sorted.length - 1];

  if (strongest.title === growthArea.title) {
    return `Your congregation shows ${SCORE_LEVEL_LABELS[
      getScoreLevel(strongest.score)
    ].toLowerCase()} in ${strongest.title.toLowerCase()}.`;
  }

  return `Your congregation shows strength in ${strongest.title.toLowerCase()} with opportunities for growth in ${growthArea.title.toLowerCase()}.`;
};

const buildKeyInsights = (categories: CategoryBreakdown[], respondentCount: number): KeyInsight[] => {
  const sorted = [...categories].sort((left, right) => right.score - left.score);
  const strongest = sorted[0];
  const growthArea = sorted[sorted.length - 1];

  return [
    {
      title: 'Total Respondents',
      icon: 'fas fa-users',
      iconBackground: Color.Blue._50,
      metric: String(respondentCount),
      description: 'Congregation members providing insight into their spiritual growth and practices',
    },
    strongest && {
      title: 'Top Performing',
      icon: 'fas fa-chart-line',
      iconBackground: Color.Primary._50,
      highlight: { label: strongest.title, value: strongest.score.toFixed(2) },
      description: buildCategoryDescription(strongest.score),
    },
    growthArea && {
      title: 'Growth Opportunity',
      icon: 'fas fa-check-circle',
      iconBackground: Color.Orange._50,
      highlight: { label: growthArea.title, value: growthArea.score.toFixed(2) },
      description: `Opportunity to strengthen discipleship practices in ${growthArea.title.toLowerCase()}.`,
    },
  ].filter(Boolean) as KeyInsight[];
};

const groupQuestionsBySubscale = (
  questions: Record<string, AssessmentReportQuestion>
): Record<string, AssessmentReportQuestion[]> =>
  Object.values(questions).reduce<Record<string, AssessmentReportQuestion[]>>((groups, question) => {
    if (!question.subscaleID) {
      return groups;
    }

    if (!groups[question.subscaleID]) {
      groups[question.subscaleID] = [];
    }

    groups[question.subscaleID].push(question);
    return groups;
  }, {});

export const transformAssessmentReport = (
  report: AssessmentReportSummaryResponse,
  assessment: AssessmentState
): AssessmentReportViewModel => {
  const { orgResult, respondentCount } = report;
  const questionsBySubscale = groupQuestionsBySubscale(orgResult.questions);

  const uncategorizedQuestions = Object.values(orgResult.questions)
    .filter(question => !question.subscaleID)
    .map(question => buildCategoryQuestion(question, respondentCount));

  const categories = Object.keys(orgResult.subscaleScores).map(subscaleId => {
    const subscale = orgResult.subscaleScores[subscaleId];
    const subscaleQuestions = questionsBySubscale[subscaleId] ?? [];
    const categoryQuestions = subscaleQuestions.map(question => buildCategoryQuestion(question, respondentCount));

    return {
      title: subscale.name,
      questionCount: categoryQuestions.length,
      description: buildCategoryDescription(subscale.score),
      idealPercent: Math.round(average(categoryQuestions.map(question => question.idealPercent))),
      positivePercent: Math.round(average(categoryQuestions.map(question => question.positivePercent))),
      score: subscale.score,
      questions: categoryQuestions,
    };
  });

  return {
    title: assessment.name,
    subtitle: report.courseName,
    introduction: assessment.introduction,
    overallScore: orgResult.overallScore,
    overallSummary: buildOverallSummary(categories),
    insights: buildKeyInsights(categories, respondentCount),
    categories,
    uncategorizedQuestions,
  };
};
